import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Save, Search, Package } from "lucide-react";
import { quotationsAPI, productsAPI, branchesAPI, getApiErrorPayload } from "@/lib/api";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { CustomerPicker } from "@/features/customers/components/CustomerPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { getDashboardProductImageSrc } from "@/lib/productImageUrl";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { SectionHead } from "@/components/ui/section-head";
import { useUnsavedChangesGuard, useIsDirty } from "@/hooks/useUnsavedChangesGuard";
import { FieldError } from "@/components/ui/field-error";

interface QItem {
	id: string;
	productId: string;
	productName: string;
	productImage?: string;
	barcode?: string;
	quantity: number;
	unitPrice: number;
	discountPercentage: number;
	discountAmount: number;
	taxPercentage: number;
	taxAmount: number;
	totalAmount: number;
}

const AddQuotationPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const isEdit = !!id;
	const { toast } = useToast();
	const { user } = useAuth();
	const currency = useCurrency();
	const [saving, setSaving] = useState(false);
	const [products, setProducts] = useState<any[]>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
	const [selectedProducts, setSelectedProducts] = useState<QItem[]>([]);
	const [branches, setBranches] = useState<{ id: number; name: string }[]>([]);
	const [branchId, setBranchId] = useState<number | "">("");
	const [customerId, setCustomerId] = useState<string>("");
	const [validUntil, setValidUntil] = useState<string>("");
	const [notes, setNotes] = useState<string>("");
	const [loaded, setLoaded] = useState(!isEdit);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	// On create the branch is preselected after loading, so it is only tracked when editing.
	const { isDirty } = useIsDirty(
		{ branchId: isEdit ? branchId : null, customerId, validUntil, notes, selectedProducts },
		loaded,
	);
	const { allowNavigation } = useUnsavedChangesGuard(isDirty && !saving);

	const clearFieldError = (field: string) => {
		setFieldErrors(prev => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	useEffect(() => {
		branchesAPI.getAll().then((list: any) => {
			const b = Array.isArray(list) ? list : [];
			setBranches(b);
			if (!b.length) return;
			setBranchId(prev => {
				if (prev !== "") return prev;
				const uid = user?.branchId;
				return uid && b.some((x: { id: number }) => x.id === uid) ? uid : b[0].id;
			});
		});
	}, [user?.branchId]);

	useEffect(() => {
		if (!isEdit || !id) return;
		quotationsAPI
			.getById(id)
			.then((q: any) => {
				if (q.branchId) setBranchId(q.branchId);
				setCustomerId(q.customerId ? String(q.customerId) : "");
				setValidUntil(q.validUntil ? String(q.validUntil).slice(0, 10) : "");
				setNotes(q.notes ?? "");
				setSelectedProducts(
					(q.items || []).map((it: any) => {
						const unitPrice = Number(it.unitPrice);
						const quantity = Number(it.quantity);
						const discountPercentage = Number(it.discountRate) || 0;
						const taxPercentage = Number(it.taxRate) || 0;
						return recalc({
							id: `${it.id}`,
							productId: String(it.productId),
							productName: it.product?.name ?? `#${it.productId}`,
							productImage: it.product?.image,
							barcode: it.product?.barcode,
							quantity,
							unitPrice,
							discountPercentage,
							discountAmount: 0,
							taxPercentage,
							taxAmount: 0,
							totalAmount: 0,
						});
					}),
				);
				setLoaded(true);
			})
			.catch(() => {
				toast({
					title: t("common.error"),
					description: t("quotations.errors.notFound"),
					variant: "destructive",
				});
				navigate("/quotations");
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	useEffect(() => {
		if (!branchId) {
			setProducts([]);
			setFilteredProducts([]);
			return;
		}
		productsAPI.getAll({ branchId, limit: 1000, page: 1 }).then((data: any) => {
			const list = data?.products ?? [];
			setProducts(list);
			setFilteredProducts(list);
		});
	}, [branchId]);

	useEffect(() => {
		if (searchQuery.trim() === "") {
			setFilteredProducts(products);
		} else {
			const q = searchQuery.toLowerCase();
			setFilteredProducts(
				products.filter(p => p.name.toLowerCase().includes(q) || p.barcode?.toLowerCase().includes(q)),
			);
		}
	}, [searchQuery, products]);

	const addProduct = (product: any) => {
		clearFieldError("items");
		const existing = selectedProducts.find(item => item.productId === product.id);
		if (existing) {
			setSelectedProducts(prev =>
				prev.map(item =>
					item.productId === product.id ? recalc({ ...item, quantity: item.quantity + 1 }) : item,
				),
			);
		} else {
			setSelectedProducts(prev => [
				...prev,
				recalc({
					id: Date.now().toString(),
					productId: product.id,
					productName: product.name,
					productImage: product.image,
					barcode: product.barcode,
					quantity: 1,
					unitPrice: product.sellingPrice || 0,
					discountPercentage: 0,
					discountAmount: 0,
					taxPercentage: Number(product.taxRate) || 0,
					taxAmount: 0,
					totalAmount: product.sellingPrice || 0,
				}),
			]);
		}
		setSearchQuery("");
		setFilteredProducts(products);
	};

	const recalc = (item: QItem): QItem => {
		const discountAmount = (item.unitPrice * item.quantity * item.discountPercentage) / 100;
		const taxAmount = ((item.unitPrice * item.quantity - discountAmount) * item.taxPercentage) / 100;
		const totalAmount = item.unitPrice * item.quantity - discountAmount + taxAmount;
		return { ...item, discountAmount, taxAmount, totalAmount };
	};

	const updateItem = (itemId: string, field: keyof QItem, value: any) => {
		setSelectedProducts(prev =>
			prev.map(item => (item.id === itemId ? recalc({ ...item, [field]: value }) : item)),
		);
	};

	const removeItem = (itemId: string) => setSelectedProducts(prev => prev.filter(i => i.id !== itemId));

	const calculateTotals = () => {
		const subtotal = selectedProducts.reduce((s, i) => s + i.unitPrice * i.quantity, 0);
		const totalDiscount = selectedProducts.reduce((s, i) => s + i.discountAmount, 0);
		const totalTax = selectedProducts.reduce((s, i) => s + i.taxAmount, 0);
		return { subtotal, totalDiscount, totalTax, total: subtotal - totalDiscount + totalTax };
	};

	const handleSubmit = async () => {
		const errors: Record<string, string> = {};
		if (!branchId) errors.branchId = "validation.required";
		if (selectedProducts.length === 0) errors.items = "quotations.errors.productsRequired";
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			toast({
				title: t("common.error"),
				description: t("validation.checkFields"),
				variant: "destructive",
			});
			return;
		}
		setFieldErrors({});
		try {
			setSaving(true);
			const itemsForApi = selectedProducts.map(item => {
				const quantity = Number(item.quantity) || 1;
				const unitPrice = Number(item.unitPrice);
				const taxRate = Number(item.taxPercentage) || 0;
				const discountRate = Number(item.discountPercentage) || 0;
				if (!Number.isFinite(unitPrice) || unitPrice < 0 || !Number.isFinite(quantity) || quantity <= 0) {
					throw new Error(t("quotations.errors.invalidItemAmounts"));
				}
				return { productId: Number(item.productId), quantity, unitPrice, taxRate, discountRate };
			});
			const payloadBody = {
				customerId: customerId ? Number(customerId) : undefined,
				branchId: branchId ? Number(branchId) : undefined,
				validUntil: validUntil || undefined,
				notes: notes.trim() || undefined,
				items: itemsForApi,
			};
			if (isEdit && id) {
				await quotationsAPI.update(id, payloadBody);
				toast({ title: t("common.success"), description: t("quotations.messages.updated") });
				allowNavigation();
				navigate(`/quotations/${id}`);
			} else {
				const created = await quotationsAPI.create(payloadBody);
				toast({ title: t("common.success"), description: t("quotations.messages.created") });
				allowNavigation();
				navigate(created?.id ? `/quotations/${created.id}` : "/quotations");
			}
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("common.errors.unexpected"),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const { subtotal, totalDiscount, totalTax, total } = calculateTotals();

	return (
		<div className="container mx-auto py-6 space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<PageHeading className="mb-2 ">
						{isEdit ? t("quotations.edit.title") : t("quotations.add.title")}
					</PageHeading>
					<p className="text-muted-foreground">{t("quotations.add.description")}</p>
				</div>
				<Button onClick={handleSubmit} disabled={saving}>
					{saving ? (
						<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>
					) : (
						<Save className="h-4 w-4 mx-2" />
					)}
					{t("quotations.add.save")}
				</Button>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				<div className="lg:col-span-2 space-y-6">
					<SectionHead num={1} title={t("quotations.add.operationData")} />
					<Card>
						<CardContent className="space-y-6 pt-6">
							<p className="text-sm text-muted-foreground">{t("quotations.add.note")}</p>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label>
										{t("quotations.add.branch")} <span className="text-destructive">*</span>
									</Label>
									<Select
										value={branchId ? String(branchId) : ""}
										onValueChange={v => {
											setBranchId(v ? parseInt(v, 10) : "");
											clearFieldError("branchId");
										}}
									>
										<SelectTrigger
											aria-label={t("quotations.add.branch")}
											aria-invalid={!!fieldErrors.branchId}
										>
											<SelectValue placeholder={t("quotations.add.selectBranch")} />
										</SelectTrigger>
										<SelectContent>
											{branches.map(b => (
												<SelectItem key={b.id} value={String(b.id)}>
													{b.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FieldError message={fieldErrors.branchId} />
								</div>
								<div className="space-y-2">
									<Label htmlFor="quotation-customer">{t("quotations.add.customerOptional")}</Label>
									<CustomerPicker
										id="quotation-customer"
										value={customerId}
										onChange={setCustomerId}
										placeholder={t("quotations.add.noCustomer")}
										clearable
									/>
								</div>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label>{t("quotations.add.validUntil")}</Label>
									<Input
										aria-label={t("quotations.add.validUntil")}
										type="date"
										value={validUntil}
										onChange={e => setValidUntil(e.target.value)}
									/>
								</div>
								<div className="space-y-2">
									<Label>{t("quotations.add.notes")}</Label>
									<Input
										aria-label={t("quotations.add.notes")}
										type="text"
										value={notes}
										onChange={e => setNotes(e.target.value)}
										placeholder={t("quotations.add.notesPlaceholder")}
									/>
								</div>
							</div>
						</CardContent>
					</Card>

					<SectionHead
						num={2}
						title={t("quotations.add.products")}
						hint={t("quotations.add.productsDescription")}
					/>
					<Card>
						<CardContent className="space-y-6 pt-6">
							<div className="space-y-2">
								<Label>{t("quotations.add.searchProducts")}</Label>
								<div className="relative">
									<Search className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
									<Input
										aria-label={t("quotations.add.searchProducts")}
										placeholder={t("quotations.add.searchPlaceholder")}
										value={searchQuery}
										onChange={e => setSearchQuery(e.target.value)}
										className="ps-10"
									/>
								</div>
							</div>

							<div className="border rounded-lg p-4 max-h-60 overflow-y-auto">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{filteredProducts.map(product => (
										<div
											key={product.id}
											className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted cursor-pointer"
											onClick={() => addProduct(product)}
										>
											<div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center overflow-hidden">
												{getDashboardProductImageSrc(product.image) ? (
													<img
														src={getDashboardProductImageSrc(product.image)}
														alt={product.name}
														className="w-full h-full object-cover rounded-lg"
													/>
												) : (
													<Package className="h-6 w-6 text-primary" />
												)}
											</div>
											<div className="flex-1">
												<h4 className="font-medium">{product.name}</h4>
												<p className="text-sm font-medium">
													{t("quotations.add.price")}:{" "}
													<Price value={product.sellingPrice || 0} currency={currency} />
												</p>
											</div>
											<Button
												title={t("common.add")}
												aria-label={t("common.add")}
												size="sm"
												variant="outline"
											>
												<Plus className="h-4 w-4" />
											</Button>
										</div>
									))}
								</div>
							</div>

							<div className="space-y-4">
								<h4 className="font-medium">
									{t("quotations.add.selectedProducts")} <span className="text-destructive">*</span>
								</h4>
								<FieldError message={fieldErrors.items} />
								{selectedProducts.map(item => (
									<div key={item.id} className="flex items-center gap-4 p-4 border rounded-lg">
										<div className="flex-1">
											<h4 className="font-medium">{item.productName}</h4>
											<div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-2">
												<div>
													<Label className="text-sm">{t("quotations.add.quantity")}</Label>
													<Input
														aria-label={t("quotations.add.quantity")}
														type="number"
														min="1"
														value={item.quantity}
														onChange={e =>
															updateItem(
																item.id,
																"quantity",
																parseInt(e.target.value) || 1,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("quotations.add.unitPrice")}</Label>
													<Input
														aria-label={t("quotations.add.unitPrice")}
														type="number"
														min="0"
														step="0.01"
														value={item.unitPrice}
														onChange={e =>
															updateItem(
																item.id,
																"unitPrice",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-24"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("quotations.add.discount")}</Label>
													<Input
														aria-label={t("quotations.add.discount")}
														type="number"
														min="0"
														max="100"
														step="0.01"
														value={item.discountPercentage}
														onChange={e =>
															updateItem(
																item.id,
																"discountPercentage",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("quotations.add.taxPercent")}</Label>
													<Input
														aria-label={t("quotations.add.taxPercent")}
														type="number"
														min="0"
														max="100"
														step="0.01"
														value={item.taxPercentage}
														onChange={e =>
															updateItem(
																item.id,
																"taxPercentage",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("quotations.add.total")}</Label>
													<div className="text-lg font-semibold">
														<Price value={item.totalAmount} currency={currency} />
													</div>
												</div>
											</div>
										</div>
										<Button
											title={t("common.delete")}
											aria-label={t("common.delete")}
											size="sm"
											variant="destructive"
											onClick={() => removeItem(item.id)}
										>
											<Trash2 className="h-4 w-4 text-white" />
										</Button>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				</div>

				<div className="space-y-6">
					<Card>
						<CardHeader>
							<CardTitle>{t("quotations.add.summary")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="flex justify-between">
								<span>{t("quotations.add.subtotal")}</span>
								<span className="font-semibold">
									<Price value={subtotal} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("quotations.add.discount")}</span>
								<span className="font-semibold text-destructive">
									-<Price value={totalDiscount} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("quotations.add.tax")}</span>
								<span className="font-semibold">
									<Price value={totalTax} currency={currency} />
								</span>
							</div>
							<div className="border-t pt-4">
								<div className="flex justify-between text-lg font-bold">
									<span>{t("quotations.add.total")}</span>
									<Price value={total} currency={currency} />
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>

			{/* Sticky action bar */}
			<div className="sticky bottom-0 z-10 flex items-center justify-end gap-2 rounded-t-lg border-t bg-background/95 px-3 py-3 backdrop-blur">
				<Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={saving}>
					{t("common.cancel")}
				</Button>
				<Button onClick={handleSubmit} disabled={saving} className="w-full sm:w-40">
					{saving ? (
						<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-2"></div>
					) : (
						<Save className="h-4 w-4 mx-2" />
					)}
					{t("common.save")}
				</Button>
			</div>
		</div>
	);
};

export default AddQuotationPage;
